import { useTradingPermissions } from '@features/dashboard/widgets/trading-v2/hooks/useTradingPermissions';
import { useApi } from '@hooks/useApi';
import { Box, Paper, Switch, Typography } from '@mui/material';
import { ServerInfo } from '@protos/v2/serverinfo';
import InlineGroup from '@shared/components/InlineGroup';
import MultilineGroup from '@shared/components/MultilineGroup';
import RoundedBox from '@shared/components/RoundedBox';
import { TextInputReadOnlyBox } from '@shared/components/TextInputReadOnlyBox';
import { useUserSettingsContext } from '@shared/contexts/UserContextProvider';
import { useScreenSize } from '@shared/hooks/useScreenSize';
import { ClientEnum, PermissionsEnum, User } from '@shared/protos/user';
import { chunkArray } from '@shared/utils/array';
import React, { useCallback, useState } from 'react';
import { ApiTokenBox } from './components/ApiTokenBox';
import { EditableUserInfo } from './components/EditableUserInfo';
import { EditButton } from './components/EditButton';
import { TradingPreferenceDialog } from './components/TradingPreferenceDialog';
import { UserInfoList } from './components/UserInfoList';
import { UserLiveWebsockets } from './components/UserLiveWebsockets';
import { convertName, getExpiryDateLabel, tradeLimitsList } from './utils';
import { checkIsInternalUser, checkIsLimitOrderTypesAllowed } from '@utils/permissions';

type Props = {
  user: User;
  streamInfo?: ServerInfo;
};

const generateUserInfo = (user: User) => {
  const isUserPremium = !user.permissions.includes(PermissionsEnum.FREEMIUM);
  const premiumUserInfo = [
    {
      label: 'Is Flux admin?',
      value: `${user.is_flux_admin}`,
    },
    {
      label: 'Is Trading admin?',
      value: `${user.is_trading_admin}`,
    },
    {
      label: 'Websocket limit',
      value: `${user.websocket_limit}`,
    },
    {
      label: 'Client type',
      value: `${user.client_type}`,
    },
  ];
  const userInfo = [
    {
      label: 'User ID',
      value: user.id,
    },
    {
      label: 'Full name',
      value: user.full_name,
    },
    {
      label: 'Account name',
      value: `${user.account_name}`,
    },
    {
      label: 'Email',
      value: user.email,
    },
    {
      label: 'Whatsapp number',
      value: user.whatsapp_number,
    },
    {
      label: 'Allow trading',
      value: `${user.allow_trading}`,
    },

    {
      label: 'Access historical data',
      value: `${user.access_historical_data}`,
    },
    {
      label: 'Access live data',
      value: `${user.access_live_data}`,
    },
  ];
  return isUserPremium ? [...premiumUserInfo, ...userInfo] : userInfo;
};

const getInputFields = (user: User) => {
  return generateUserInfo(user).map((data, idx) => <TextInputReadOnlyBox label={data.label} value={data.value || '-'} key={`user_info_${idx}`} />);
};

export const UserProfileForm: React.FC<Props> = ({ user, streamInfo }) => {
  const { userTradeLimits, tradingAccounts } = useTradingPermissions(user);
  const { apiClient } = useApi();
  const { isMobile } = useScreenSize();
  const inputs = getInputFields(user);
  const [apiToken, setApiToken] = useState(user.api_token);
  const [isExchangeOrderDialogOpen, setIsExchangeOrderDialogOpen] = useState(false);
  const [localTradeLimits, setLocalTradeLimits] = useState(userTradeLimits);
  const [localTradeAccounts, setLocalTradeAccounts] = useState(tradingAccounts || []);
  const [isEditingTradeAccounts, setIsEditingTradeAccounts] = useState(false);
  const [originalTradeAccounts, setOriginalTradeAccounts] = useState<string[]>([]);
  const context = useUserSettingsContext();
  const updateUserSettings = context?.updateUserSettings;
  const userSettings = context?.userSettings;
  const isUserExternal = !checkIsInternalUser(user);
  const userShowTradeConfirmationSetting = userSettings?.widgets?.trading_v2?.showConfirmationOnTrade;
  const showTradeConfirmation = userShowTradeConfirmationSetting ?? isUserExternal;
  const isPremiumUser = !user.permissions.includes(PermissionsEnum.FREEMIUM);
  const isLimitOrderTypesAllowed = checkIsLimitOrderTypesAllowed(user);

  const TradingAccountsList = useCallback(
    ({ user }: { user: User }) => {
      if (!user.allow_trading) return null;

      const handleEditTradeAccounts = () => {
        setOriginalTradeAccounts([...localTradeAccounts]);
        setIsEditingTradeAccounts(true);
      };

      const handleSaveTradeAccounts = async () => {
        if (!apiClient) return;
        if (!userSettings) return;
        await updateUserSettings?.({
          ...userSettings,
          ocl_trading: {
            ...userSettings.ocl_trading,
            trade_accounts: localTradeAccounts,
          },
        });
        setIsEditingTradeAccounts(false);
      };

      const handleCancelTradeAccounts = () => {
        setLocalTradeAccounts([...originalTradeAccounts]);
        setIsEditingTradeAccounts(false);
      };

      const handleTradeAccountsOrderChange = (newOrder: string[]) => {
        setLocalTradeAccounts(newOrder);
      };

      return (
        <>
          <EditableUserInfo
            list={localTradeAccounts}
            header={'Trading Accounts'}
            onOrderChange={handleTradeAccountsOrderChange}
            isEditing={isEditingTradeAccounts}
            onSave={handleSaveTradeAccounts}
            onCancel={handleCancelTradeAccounts}
            extraOptions={localTradeAccounts.length > 0 ? <EditButton onClick={() => handleEditTradeAccounts()} /> : undefined}
            showDivider={true}
          />
        </>
      );
    },
    [apiClient, localTradeAccounts, isEditingTradeAccounts, originalTradeAccounts, user, localTradeLimits, updateUserSettings, userSettings]
  );

  const toggleTradeConfirmationDialog = useCallback(() => {
    if (!updateUserSettings || !userSettings) return;
    updateUserSettings({
      ...userSettings,
      widgets: {
        ...userSettings.widgets,
        trading_v2: {
          ...userSettings.widgets?.trading_v2,
          showConfirmationOnTrade: !showTradeConfirmation,
        },
      },
    });
  }, [updateUserSettings, userSettings, showTradeConfirmation]);

  const recreateToken = async () => {
    if (!apiClient) return;
    const response = await apiClient.refreshApiToken();
    setApiToken(response.api_token);
  };

  const handleEditExchangeOrder = () => {
    setIsExchangeOrderDialogOpen(true);
  };

  const handleCloseExchangeOrderDialog = (updatedLimits?: any[]) => {
    setIsExchangeOrderDialogOpen(false);
    if (updatedLimits) {
      setLocalTradeLimits(updatedLimits);
    }
  };

  const numberOfColumns = isMobile ? 1 : 2;

  const chunks = chunkArray(inputs, numberOfColumns);
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Paper style={{ padding: 20 }}>
        <MultilineGroup>
          <Typography variant="h3" gutterBottom sx={{ marginBottom: '20px' }}>
            Account Info
          </Typography>
          {chunks.map((chunk, idx) => (
            <InlineGroup key={`group_${idx}`}>{chunk}</InlineGroup>
          ))}
        </MultilineGroup>
      </Paper>

      <Paper style={{ padding: 20 }}>
        {user.client_type_list.includes(ClientEnum.API) && (
          <MultilineGroup>
            <InlineGroup>
              <ApiTokenBox recreateToken={recreateToken} label={'API token'} apiToken={apiToken} key={'api_token'} />
            </InlineGroup>
          </MultilineGroup>
        )}
        <MultilineGroup>
          {chunkArray(
            [
              <TextInputReadOnlyBox label={'Licence type'} value={convertName(user.license_type)} key={'licence_type'} />,
              <TextInputReadOnlyBox
                label={'License expiration date'}
                value={getExpiryDateLabel(user.license_expiry, isMobile)}
                key={'licence_expiration_date'}
              />,
            ],
            numberOfColumns
          ).map((chunk, idx) => (
            <InlineGroup key={`group_licence_${idx}`}>{chunk}</InlineGroup>
          ))}
        </MultilineGroup>
      </Paper>
      {isPremiumUser && (
        <Paper style={{ padding: 20 }}>
          <RoundedBox>
            <UserInfoList list={user.permissions} header={'Permissions'} showDivider={true} />
            <UserInfoList list={user.product_groups} header={'Product Groups'} showDivider={true} />
            <UserInfoList list={user.cot_product_groups} header={'COT Product Groups'} showDivider={true} />
            <UserInfoList list={user.client_type_list} header={'Client type'} showDivider={true} />
            <UserInfoList list={user.snapshot_periods} header={'Snapshot periods'} showDivider={true} />
            {user.permissions.includes(PermissionsEnum.LADDER) && user.ladder_limits.length > 0 && (
              <UserInfoList
                list={user.ladder_limits.map(({ symbol, limit }) => `${symbol.toLocaleUpperCase()} : ${limit}`)}
                header={'Ladder Limits'}
                helperText={'Below is formatted as - "Symbol : Ladder Input Limit"'}
                showDivider={true}
              />
            )}
            <UserInfoList
              list={tradeLimitsList(localTradeLimits, isLimitOrderTypesAllowed)}
              header={'Trading Limits'}
              helperText={`Below is formatted as - "Symbol : Trading Size Limit : Exchanges ${isLimitOrderTypesAllowed ? ' : Order Types' : ''}"`}
              showDivider={true}
              extraOptions={<EditButton onClick={() => handleEditExchangeOrder()} />}
            />
            <TradingAccountsList user={user} />
            <UserInfoList
              list={showTradeConfirmation ? ['Enabled'] : ['Disabled']}
              header={'Trade Confirmation'}
              extraOptions={<Switch checked={showTradeConfirmation} onClick={() => toggleTradeConfirmationDialog()} />}
            />
            {streamInfo && <UserLiveWebsockets streamInfo={streamInfo} />}
          </RoundedBox>
        </Paper>
      )}
      {isPremiumUser && (
        <TradingPreferenceDialog open={isExchangeOrderDialogOpen} onClose={handleCloseExchangeOrderDialog} tradeLimits={localTradeLimits} />
      )}
    </Box>
  );
};
